import { Check, ChevronDown, ChevronUp, Search } from "lucide-react";
import React, { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import { cn } from "../../../../lib/utils";

interface MultiSelectSummaryDropdownProps<T = string> {
  name?: string;
  value?: T[];
  options?: OptionNameValuePair<T>[];
  onChange?: (value: T[]) => void;
  placeholder?: string;
  enableSearch?: boolean;
  disabled?: boolean;
  showChipSummary?: boolean;
  showTooltip?: boolean;
  hideChevronWhenSelected?: boolean;
  className?: string;
  chipClassName?: string;
  rootContainerClassName?: string;
  dropdownClassName?: string;
  searchPlaceholder?: string;
  renderChipText?: (selectedOptions: OptionNameValuePair<T>[]) => string | null;
  renderTooltipText?: (selectedOptions: OptionNameValuePair<T>[]) => string;
  renderPlaceholder?: () => React.ReactNode;
  renderCustomView?: () => React.ReactNode;
}

function MultiSelectSummaryDropdown<T = string>({
  name = "items",
  value = [],
  options = [],
  onChange,
  placeholder,
  enableSearch = true,
  disabled = false,
  showChipSummary = true,
  showTooltip = true,
  hideChevronWhenSelected = true,
  className,
  chipClassName,
  rootContainerClassName,
  dropdownClassName,
  searchPlaceholder,
  renderChipText,
  renderTooltipText,
  renderPlaceholder,
  renderCustomView,
}: MultiSelectSummaryDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [visibleOptions, setVisibleOptions] =
    useState<OptionNameValuePair<T>[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties | null>(
    null
  );

  const selectedValues = Array.isArray(value) ? value : [];
  const mappedSelected = options?.filter((opt) =>
    selectedValues.includes(opt.value)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropdownMenuRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setVisibleOptions(options);
  }, [options]);

  const toggleIsOpen = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (next) queueMicrotask(() => updateMenuPosition());
      return next;
    });
  };

  const filterOptions = (event: ChangeEvent<HTMLInputElement>) => {
    const filtered = options.filter((option) =>
      option.name.toLowerCase().startsWith(event.target.value.toLowerCase())
    );
    setVisibleOptions(filtered);
  };

  const toggleOption = (optionValue: T) => {
    if (disabled) return;
    const updatedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange?.(updatedValues);
  };

  const getChipText = (): string | null =>
    renderChipText
      ? renderChipText(mappedSelected)
      : mappedSelected.length
        ? mappedSelected.length === 1
          ? mappedSelected[0].name
          : `${mappedSelected[0].name} + ${mappedSelected.length - 1}`
        : null;

  const getTooltipText = (): string =>
    renderTooltipText
      ? renderTooltipText(mappedSelected)
      : mappedSelected.map((opt) => opt.name).join(", ");

  const hideChevron = hideChevronWhenSelected;
  const chipText = getChipText();
  const tooltipText = getTooltipText();
  const defaultPlaceholder = placeholder || `Select ${name}`;
  const defaultSearchPlaceholder =
    searchPlaceholder || `Search ${name.toLowerCase()}`;

  const renderChipOrPlaceholder = () => {
    if (renderCustomView) {
      return renderCustomView();
    } else if (chipText && showChipSummary) {
      return (
        <span
          className={cn(
            "inline-flex items-start min-h-5 px-2 py-0.5 gap-1 rounded-md border bg-[#EBEEFE]",
            chipClassName
          )}
        >
          <span className="font-sans font-medium text-xs leading-[18px] text-[#304ADD] break-words">
            {chipText}
          </span>
        </span>
      );
    } else if (mappedSelected.length > 0 && !showChipSummary) {
      return (
        <span className="text-sm text-gray-900">
          {mappedSelected.map((opt) => opt.name).join(", ")}
        </span>
      );
    } else if (renderPlaceholder) {
      return renderPlaceholder();
    } else {
      return (
        <span className="text-sm text-gray-500">{defaultPlaceholder}</span>
      );
    }
  };

  const updateTooltipPosition = () => {
    const anchor = dropdownRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setTooltipStyle({
      position: "fixed",
      left: rect.left,
      top: rect.bottom + 4,
      zIndex: 9999,
      maxWidth: 384,
    });
  };

  const renderTooltip = () => {
    if (
      !showTooltipState ||
      !showTooltip ||
      mappedSelected.length <= 1 ||
      !tooltipStyle
    ) {
      return null;
    }
    return ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className="absolute z-[60] max-w-[384px] bg-[#374151] text-white text-xs rounded-md py-1.5 px-3 mt-1 left-0 top-full shadow-lg whitespace-normal"
      >
        {tooltipText}
      </div>,
      document.body
    );
  };

  const renderDropdownSearch = () => {
    if (!enableSearch) return null;
    return (
      <div className="sticky top-0 bg-white p-1">
        <div className="relative">
          <Search className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
          <input
            className="w-full pl-7 pr-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            placeholder={defaultSearchPlaceholder}
            type="text"
            onChange={filterOptions}
          />
        </div>
      </div>
    );
  };

  const renderDropdownOptions = () => (
    <div className="p-1 pb-2 space-y-1">
      {visibleOptions?.map((option) => {
        const isSelected = selectedValues.includes(option.value);
        return (
          <div
            key={String(option.value)}
            className={cn(
              "rounded-md text-sm px-4 py-1.5 flex items-center cursor-pointer hover:bg-gray-100 whitespace-nowrap",
              option.disabled && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => {
              if (!option.disabled) toggleOption(option.value);
            }}
          >
            <div
              className={cn(
                "w-4 h-4 border rounded flex items-center justify-center mr-2",
                isSelected ? "bg-[#3551F3] border-[#3551F3]" : "border-gray-300"
              )}
            >
              {isSelected && (
                <Check
                  className={cn(
                    "h-3 w-3 text-white bg-[#3551F3] border-[#3551F3]"
                  )}
                />
              )}
            </div>
            {option.display_component ? (
              option.display_component
            ) : (
              <span className="flex-1">{option.name}</span>
            )}
          </div>
        );
      })}
      {visibleOptions.length === 0 && (
        <div className="text-sm text-gray-500 text-center py-2">
          No options found
        </div>
      )}
    </div>
  );

  const renderDropdown = () => {
    if (!isOpen) return null;
    if (!menuStyle) return null;
    return ReactDOM.createPortal(
      <div
        ref={dropdownMenuRef}
        style={menuStyle}
        className={cn(
          "absolute left-0 top-full z-50 mt-1 w-max min-w-full max-w-none bg-white border border-gray-300 rounded-md shadow-overviewCard max-h-60 overflow-auto",
          dropdownClassName
        )}
      >
        {renderDropdownSearch()}
        {renderDropdownOptions()}
      </div>,
      document.body
    );
  };

  const updateMenuPosition = () => {
    const anchor = dropdownRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const GAP_PX = 1;
    const VIEWPORT_PADDING_PX = 8;
    const MIN_MENU_HEIGHT_PX = 180;
    const PREFERRED_MENU_HEIGHT_PX = enableSearch ? 360 : 300;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlipToTop =
      spaceBelow < PREFERRED_MENU_HEIGHT_PX && spaceAbove > spaceBelow;

    const available = (shouldFlipToTop ? spaceAbove : spaceBelow) - GAP_PX;
    const maxHeight = Math.max(
      MIN_MENU_HEIGHT_PX,
      Math.floor(Math.min(PREFERRED_MENU_HEIGHT_PX, available - VIEWPORT_PADDING_PX))
    );

    const base: React.CSSProperties = {
      position: "fixed",
      left: rect.left,
      zIndex: 9999,
      minWidth: rect.width,
      minHeight: 0,
      maxHeight,
      overflowY: "auto",
    };

    if (shouldFlipToTop) {
      base.top = Math.max(VIEWPORT_PADDING_PX, rect.top - GAP_PX);
      base.transform = "translateY(-100%)";
    } else {
      base.top = Math.min(window.innerHeight - VIEWPORT_PADDING_PX, rect.bottom + GAP_PX);
      base.transform = undefined;
    }

    setMenuStyle(base);
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dropdownClassName, options.length, selectedValues.length]);

  useLayoutEffect(() => {
    if (!showTooltipState || !showTooltip || mappedSelected.length <= 1) return;
    updateTooltipPosition();
    const onScrollOrResize = () => updateTooltipPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTooltipState, showTooltip, mappedSelected.length, tooltipText]);

  /** Main Render **/

  return (
    <div
      ref={dropdownRef}
      className={cn("relative w-full", rootContainerClassName)}
    >
      <div
        className={cn(
          "flex items-center justify-between w-full rounded cursor-pointer py-1",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        onClick={toggleIsOpen}
        onMouseEnter={() => {
          if (showTooltip && mappedSelected.length > 1) {
            setShowTooltipState(true);
            queueMicrotask(() => updateTooltipPosition());
          }
        }}
        onMouseLeave={() => setShowTooltipState(false)}
      >
        {renderChipOrPlaceholder()}
        <div className="flex items-center">
          {!disabled &&
            !hideChevron &&
            (isOpen ? (
              <ChevronUp className="ml-2 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="ml-2 h-4 text-gray-500" />
            ))}
        </div>
      </div>

      {renderTooltip()}
      {renderDropdown()}
    </div>
  );
}

export default MultiSelectSummaryDropdown;
